import {
  applyDashboardPromotionCode,
  fetchOrderSkipDiscount,
  restoreOrder,
  skipOrder,
  type DashboardExperienceOrderResponse,
  type DashboardExperienceOrderSkipDiscountResponse,
} from '@library/api-dashboard-experience'
import React, { useEffect } from 'react'
import { browserHistory } from 'react-router'
import { useSWRConfig } from 'swr'

import { useDashboardExperienceApiConfig } from './useDashboardExperienceApiConfig'
import { useOrderTileTelemetryTracking } from './useOrderTileTelemetryTracking'
import { useOrderTracking } from './useOrderTracking'

type UseOrderTileSkipFlowParams = {
  order: DashboardExperienceOrderResponse
  displayedOrder: DashboardExperienceOrderResponse
  onSkipSuccess?: (orderId: string) => void
  setDisplayedOrder: React.Dispatch<React.SetStateAction<DashboardExperienceOrderResponse>>
}

type UseOrderTileSkipFlowResult = {
  handleApplySkipDiscountClick: () => Promise<void>
  handleConfirmSkipBoxClick: (
    skipDiscountOffer?: DashboardExperienceOrderSkipDiscountResponse | null,
  ) => Promise<void>
  handleRestoreOrderClick: () => Promise<void>
  handleSkipBoxRequestClick: () => Promise<void>
  handleSkipDiscountModalClose: () => void
  handleSkipDiscountModalView: () => void
  hasApplySkipDiscountError: boolean
  hasSkipOrderError: boolean
  isApplyingSkipDiscount: boolean
  isFetchingSkipDiscount: boolean
  isRestoringOrder: boolean
  isSkipDiscountModalOpen: boolean
  isSkippingOrder: boolean
  skipDiscount: DashboardExperienceOrderSkipDiscountResponse | null
}

export const useOrderTileSkipFlow = ({
  order,
  displayedOrder,
  onSkipSuccess,
  setDisplayedOrder,
}: UseOrderTileSkipFlowParams): UseOrderTileSkipFlowResult => {
  const {
    trackCancelOrderClick,
    trackOrderKept,
    trackOrderSkipComplete,
    trackPauseDiscountReminderOfferScreenView,
    trackRestoreOrderClick,
  } = useOrderTracking()
  const {
    trackTelemetryForApplySkipDiscountError,
    trackTelemetryForApplySkipDiscountSuccess,
    trackTelemetryForRestoreOrderError,
    trackTelemetryForRestoreOrderSuccess,
    trackTelemetryForSkipDiscountLookupError,
    trackTelemetryForSkipDiscountOfferAvailable,
    trackTelemetryForSkipDiscountOfferUnavailable,
    trackTelemetryForSkipOrderError,
    trackTelemetryForSkipOrderSuccess,
  } = useOrderTileTelemetryTracking({
    order: displayedOrder,
  })
  const { mutate } = useSWRConfig()
  const [isRestoringOrder, setIsRestoringOrder] = React.useState(false)
  const [isApplyingSkipDiscount, setIsApplyingSkipDiscount] = React.useState(false)
  const [isSkippingOrder, setIsSkippingOrder] = React.useState(false)
  const [isFetchingSkipDiscount, setIsFetchingSkipDiscount] = React.useState(false)
  const [isSkipDiscountModalOpen, setIsSkipDiscountModalOpen] = React.useState(false)
  const [skipDiscount, setSkipDiscount] =
    React.useState<DashboardExperienceOrderSkipDiscountResponse | null>(null)
  const [hasApplySkipDiscountError, setHasApplySkipDiscountError] = React.useState(false)
  const [hasSkipOrderError, setHasSkipOrderError] = React.useState(false)
  const { cacheKey: dashboardExperienceCacheKey } = useDashboardExperienceApiConfig()

  const resetSkipFlowState = React.useCallback(() => {
    setHasApplySkipDiscountError(false)
    setHasSkipOrderError(false)
    setIsApplyingSkipDiscount(false)
    setIsRestoringOrder(false)
    setIsFetchingSkipDiscount(false)
    setIsSkipDiscountModalOpen(false)
    setSkipDiscount(null)
  }, [])

  useEffect(() => {
    resetSkipFlowState()
  }, [order, resetSkipFlowState])

  const handleRestoreOrderClick = React.useCallback(async () => {
    if (isRestoringOrder) {
      return
    }

    const orderId = displayedOrder.id

    trackRestoreOrderClick({
      order: displayedOrder,
    })
    setIsRestoringOrder(true)

    try {
      await restoreOrder(orderId)
      trackTelemetryForRestoreOrderSuccess()
      await mutate(dashboardExperienceCacheKey)
    } catch (error) {
      trackTelemetryForRestoreOrderError(error)
    } finally {
      setIsRestoringOrder(false)
    }
  }, [
    displayedOrder,
    dashboardExperienceCacheKey,
    isRestoringOrder,
    mutate,
    trackRestoreOrderClick,
    trackTelemetryForRestoreOrderError,
    trackTelemetryForRestoreOrderSuccess,
  ])

  const handleConfirmSkipBoxClick = React.useCallback(
    async (
      skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse | null = skipDiscount,
    ) => {
      if (isSkippingOrder) {
        return
      }

      const orderId = displayedOrder.id

      setHasApplySkipDiscountError(false)
      setHasSkipOrderError(false)
      setIsSkippingOrder(true)

      try {
        await skipOrder(orderId)
        trackOrderSkipComplete({
          order: displayedOrder,
          skipDiscountOffer,
        })
        trackTelemetryForSkipOrderSuccess()
        setDisplayedOrder((currentOrder) => ({
          ...currentOrder,
          help_cta: null,
          restore_cta: currentOrder.restore_cta ?? 'Restore order',
          state: 'skip',
          state_title: 'Skipped',
        }))
        onSkipSuccess?.(orderId)
        await mutate(dashboardExperienceCacheKey)
        setIsSkipDiscountModalOpen(false)
        setSkipDiscount(null)
      } catch (error) {
        trackTelemetryForSkipOrderError(error)
        setHasSkipOrderError(true)
      } finally {
        setIsSkippingOrder(false)
      }
    },
    [
      displayedOrder,
      dashboardExperienceCacheKey,
      isSkippingOrder,
      mutate,
      onSkipSuccess,
      setDisplayedOrder,
      skipDiscount,
      trackTelemetryForSkipOrderError,
      trackTelemetryForSkipOrderSuccess,
      trackOrderSkipComplete,
    ],
  )

  const handleSkipBoxRequestClick = React.useCallback(async () => {
    if (isSkippingOrder || isFetchingSkipDiscount) {
      return
    }
    const orderId = displayedOrder.id

    trackCancelOrderClick({
      order: displayedOrder,
    })
    setHasApplySkipDiscountError(false)
    setHasSkipOrderError(false)
    setIsFetchingSkipDiscount(true)
    setSkipDiscount(null)

    try {
      const orderSkipDiscount = await fetchOrderSkipDiscount(orderId)

      if (orderSkipDiscount) {
        trackTelemetryForSkipDiscountOfferAvailable(orderSkipDiscount)
        setSkipDiscount(orderSkipDiscount)
        setIsSkipDiscountModalOpen(true)

        return
      }

      trackTelemetryForSkipDiscountOfferUnavailable()
      trackPauseDiscountReminderOfferScreenView({
        modalShown: false,
        order: displayedOrder,
        skipDiscountOffer: null,
      })
    } catch (error) {
      trackTelemetryForSkipDiscountLookupError(error)
      trackPauseDiscountReminderOfferScreenView({
        modalShown: false,
        order: displayedOrder,
        skipDiscountOffer: null,
      })
      // If the discount lookup is unavailable or malformed, keep the skip flow unblocked.
    } finally {
      setIsFetchingSkipDiscount(false)
    }

    await handleConfirmSkipBoxClick(null)
  }, [
    displayedOrder,
    handleConfirmSkipBoxClick,
    isFetchingSkipDiscount,
    isSkippingOrder,
    trackCancelOrderClick,
    trackPauseDiscountReminderOfferScreenView,
    trackTelemetryForSkipDiscountLookupError,
    trackTelemetryForSkipDiscountOfferAvailable,
    trackTelemetryForSkipDiscountOfferUnavailable,
  ])

  const handleSkipDiscountModalClose = React.useCallback(() => {
    if (isSkippingOrder || isApplyingSkipDiscount) {
      return
    }

    resetSkipFlowState()
  }, [isApplyingSkipDiscount, isSkippingOrder, resetSkipFlowState])

  const handleSkipDiscountModalView = React.useCallback(() => {
    if (!skipDiscount) {
      return
    }

    trackPauseDiscountReminderOfferScreenView({
      modalShown: true,
      order: displayedOrder,
      skipDiscountOffer: skipDiscount,
    })
  }, [displayedOrder, skipDiscount, trackPauseDiscountReminderOfferScreenView])

  const handleApplySkipDiscountClick = React.useCallback(async () => {
    if (isApplyingSkipDiscount || isSkippingOrder) {
      return
    }

    if (skipDiscount?.isExistingDiscount) {
      trackOrderKept({
        order: displayedOrder,
        skipDiscountOffer: skipDiscount,
      })
      browserHistory.push(`/menu/${displayedOrder.id}`)

      return
    }

    const promoCode = skipDiscount?.promoCode?.trim()

    if (!promoCode) {
      setHasApplySkipDiscountError(true)

      return
    }

    setHasApplySkipDiscountError(false)
    setIsApplyingSkipDiscount(true)

    try {
      const applyPromotionCodeResponse = await applyDashboardPromotionCode({ promoCode })
      trackTelemetryForApplySkipDiscountSuccess({
        promoCode,
        status: applyPromotionCodeResponse.status,
      })
      trackOrderKept({
        order: displayedOrder,
        skipDiscountOffer: skipDiscount,
      })
      await mutate(dashboardExperienceCacheKey)
      setIsSkipDiscountModalOpen(false)
      setSkipDiscount(null)
    } catch (error) {
      trackTelemetryForApplySkipDiscountError({
        error,
        promoCode,
      })
      setHasApplySkipDiscountError(true)
    } finally {
      setIsApplyingSkipDiscount(false)
    }
  }, [
    displayedOrder,
    dashboardExperienceCacheKey,
    isApplyingSkipDiscount,
    isSkippingOrder,
    mutate,
    skipDiscount,
    trackTelemetryForApplySkipDiscountError,
    trackTelemetryForApplySkipDiscountSuccess,
    trackOrderKept,
  ])

  return {
    handleApplySkipDiscountClick,
    handleConfirmSkipBoxClick,
    handleRestoreOrderClick,
    handleSkipBoxRequestClick,
    handleSkipDiscountModalClose,
    handleSkipDiscountModalView,
    hasApplySkipDiscountError,
    hasSkipOrderError,
    isApplyingSkipDiscount,
    isFetchingSkipDiscount,
    isRestoringOrder,
    isSkipDiscountModalOpen,
    isSkippingOrder,
    skipDiscount,
  }
}
